import React, { useCallback, useEffect, useState } from "react";
import { Input, Typography, Tag, Button, Alert, message, Tooltip, Select, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import styled from "@emotion/styled";
import { CustomTable } from "../series/StyledComponent";
import useScreenSizeHook from "../utils/useScreenSizeHook";
import { listEpisodes, getEpisodeSummary, EpisodeListRow, EpisodeSummary } from "./api";
import EpisodeDrawer from "./EpisodeDrawer";

const { Title, Paragraph } = Typography;

// CustomTable hard-codes .ant-table to ~full viewport height; this page has a
// title + summary + filters above it, so let the table size to its scroll.y
// instead, keeping the pagination bar right below the rows (no page scroll).
const TableWrap = styled.div`
  & .ant-table {
    height: auto !important;
  }
`;

type StatusKey = "ready" | "pending" | "skipped" | "not_processed";

interface Status {
  label: string;
  color: string;
  detail: string;
}

/**
 * Derive an episode's caption-pipeline status from its caption chunks: how many
 * chunks exist and how many are embedded, plus the skip reason recorded by the
 * chunker when none could be produced.
 */
const statusOf = (r: EpisodeListRow): Status => {
  const count = Number(r.CHUNK_COUNT) || 0;
  const embedded = Number(r.CHUNKS_EMBEDDED) || 0;
  if (count === 0) {
    if (r.CHUNK_SKIP_REASON) return { label: "Skipped", color: "red", detail: r.CHUNK_SKIP_REASON };
    return { label: "Not processed", color: "default", detail: "No caption chunks yet" };
  }
  if (embedded >= count) return { label: "Ready", color: "green", detail: "Chunked and embedded" };
  return { label: "Embedding pending", color: "orange", detail: `${embedded}/${count} chunks embedded` };
};

const EpisodeIndexPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [episodes, setEpisodes] = useState<EpisodeListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusKey | "all">("all");

  const [summary, setSummary] = useState<EpisodeSummary | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeRefId, setActiveRefId] = useState<string | null>(null);
  const { height } = useScreenSizeHook();

  // Debounce the search box so we don't fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter change resets to the first page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  const fetchPage = useCallback(() => {
    setLoading(true);
    listEpisodes({ page, pageSize, search: debouncedSearch, status: statusFilter })
      .then((d) => {
        setEpisodes(d.episodes || []);
        setTotal(d.total || 0);
      })
      .catch(() => message.error("Failed to load episodes"))
      .finally(() => setLoading(false));
  }, [page, pageSize, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const loadSummary = useCallback(() => {
    getEpisodeSummary().then(setSummary).catch(() => undefined);
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const refresh = () => {
    fetchPage();
    loadSummary();
  };

  const open = (refId: string) => {
    setActiveRefId(refId);
    setDrawerOpen(true);
  };

  const columns: ColumnsType<EpisodeListRow> = [
    { title: "REF ID", dataIndex: "REF_ID", key: "REF_ID", width: 140 },
    { title: "Title", dataIndex: "TITLE", key: "TITLE", ellipsis: true },
    { title: "Season", dataIndex: "SEASON", key: "SEASON", width: 80 },
    { title: "Episode", dataIndex: "EPISODE_NUMBER", key: "EPISODE_NUMBER", width: 80 },
    {
      title: "Chunks (embedded / total)",
      key: "chunks",
      width: 180,
      render: (_, r) => {
        const count = Number(r.CHUNK_COUNT) || 0;
        return count === 0 ? "0" : `${Number(r.CHUNKS_EMBEDDED) || 0} / ${count}`;
      },
    },
    {
      title: "Caption status",
      key: "status",
      width: 200,
      render: (_, r) => {
        const s = statusOf(r);
        return (
          <Tooltip title={s.detail}>
            <Tag color={s.color}>{s.label}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      render: (_, r) => (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            open(r.REF_ID);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const allReady =
    summary !== null && summary.pending === 0 && summary.skipped === 0 && summary.not_processed === 0;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }} align="start">
        <div>
          <Title level={3}>Episode search index</Title>
          <Paragraph type="secondary">Audit tool — look up any episode to see its caption-search data</Paragraph>
        </div>
        <Button onClick={refresh} loading={loading}>
          Refresh
        </Button>
      </Space>

      {summary && (
        <Alert
          type={allReady ? "success" : "warning"}
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <Space size="large" wrap>
              <span>✅ Ready: {summary.ready}</span>
              <span>🟠 Embedding pending: {summary.pending}</span>
              <span>⛔ Skipped (no caption): {summary.skipped}</span>
              <span>⬜ Not processed: {summary.not_processed}</span>
            </Space>
          }
          description={
            summary.pending > 0
              ? "Some episodes have caption chunks that are not embedded yet — run the embedding pipeline."
              : summary.not_processed > 0
                ? "Some episodes have no chunks yet — run the caption chunker."
                : undefined
          }
        />
      )}

      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          placeholder="Filter by title or REF ID"
          allowClear
          style={{ width: 360 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          value={statusFilter}
          style={{ width: 220 }}
          onChange={setStatusFilter}
          options={[
            { value: "all", label: "All statuses" },
            { value: "ready", label: "Ready" },
            { value: "pending", label: "Embedding pending" },
            { value: "skipped", label: "Skipped (no caption)" },
            { value: "not_processed", label: "Not processed" },
          ]}
        />
      </Space>

      <TableWrap>
        <CustomTable
          rowKey="REF_ID"
          loading={loading}
          columns={columns as any}
          dataSource={episodes}
          size="middle"
          scroll={{ y: height - 360 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (t) => `${t} episodes`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          onRow={(record: any) => ({ onClick: () => open(record.REF_ID), style: { cursor: "pointer" } })}
        />
      </TableWrap>

      <EpisodeDrawer open={drawerOpen} refId={activeRefId} onClose={() => setDrawerOpen(false)} />
    </div>
  );
};

export default EpisodeIndexPage;
